import cx from 'classnames';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import Layout from '../../../layout/default';
import { getPickupShiftCellsService } from '../../../services/scheduling';
import { getRoleLabelsService } from '../../../services/company';
import { getAllDaysInMonth } from '../../../utility';
import { isResponseOk } from '../../../utility/isResponseOk';
import ConfirmPickupShiftModal from './components/ConfirmPickupShiftModal';
import Header from './components/Header';
import './styles.scss';
import { useAuth } from '../../../container/authContext';
import { convertTimeRangeTo12Hour, getDateFromString } from '../../../utility';
import { usePermission } from '../../../container/permissionContext';
import { groupPostedSlots, isSlotOpenForUser } from '../../../utility/pickupShiftSlots';

const PickupShifts = () => {
  const { user } = useAuth();
  const today = new Date();
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [roleLabels, setRoleLabels] = useState({});

  const { hasPermissionByCodeName } = usePermission();

  const [pickupShiftData, setPickupShiftData] = useState([]);
  const [confirmPickupData, setConfirmPickupData] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const hasPickupShiftPermission = hasPermissionByCodeName('shift_pickup_for_nurses') || hasPermissionByCodeName('shift_pickup_for_cnas');

  useEffect(() => {
    getRoleLabelsService(user?.company?.id).then((res) => {
      setRoleLabels(res.data);
    });
  }, [user?.company?.id]);

  const calendarDaysData = useMemo(() => {
    const emptyStartDays = Array.from({ length: selectedDate.getDay() }, (_, i) => ({ date: null, weekDay: i }));

    const allDays = getAllDaysInMonth(selectedDate.getMonth() + 1, selectedDate.getFullYear());
    const calendarDays = allDays.map((day) => {
      const obj = {
        date: day,
        weekDay: day.getDay(),
        cells: pickupShiftData.filter((cell) => getDateFromString(cell.date).toLocaleDateString() === day.toLocaleDateString()),
      };

      obj.takenShiftsData = obj.cells
        .filter((cell) => cell.first_user_id === user.id || cell.second_user_id === user.id || cell.both_user_id === user.id)
        .map((cell) => ({
          shiftName: cell.shift_name,
          isFirstPartTaken: cell.first_user_id === user.id,
          isSecondPartTaken: cell.second_user_id === user.id,
          isBothPartsTaken: cell.both_user_id === user.id,
        }));

      obj.postedSlots = groupPostedSlots(obj.cells, user.id);

      return obj;
    });

    const emptyEndDays = Array.from({ length: 6 - allDays.at(-1).getDay() }, (_, i) => ({
      date: null,
      weekDay: allDays.at(-1).getDay() + i + 1,
    }));

    return [...emptyStartDays, ...calendarDays, ...emptyEndDays];
  }, [selectedDate, pickupShiftData, user.id]);

  const selectedDayCellsData = useMemo(() => {
    if (!selectedDay) return { cells: [], takenShiftsForDayData: [], postedSlots: [] };

    const cellsData = {
      cells: pickupShiftData.filter((cell) => getDateFromString(cell.date).toLocaleDateString() === selectedDay.toLocaleDateString()),
    };

    cellsData.takenShiftsForDayData = cellsData.cells
      .filter((cell) => cell.first_user_id === user.id || cell.second_user_id === user.id || cell.both_user_id === user.id)
      .map((cell) => ({
        shiftName: cell.shift_name,
        isFirstPartTaken: cell.first_user_id === user.id,
        isSecondPartTaken: cell.second_user_id === user.id,
        isBothPartsTaken: cell.both_user_id === user.id,
      }));

    cellsData.postedSlots = groupPostedSlots(cellsData.cells, user.id);

    return cellsData;
  }, [selectedDay, pickupShiftData, user]);

  const getPickupShiftData = useCallback(async () => {
    try {
      const params = {
        start_date: dayjs(selectedDate).startOf('month').format('YYYY-MM-DD'),
        end_date: dayjs(selectedDate).endOf('month').format('YYYY-MM-DD'),
      };
      const response = await getPickupShiftCellsService(params);
      if (isResponseOk(response) && response.data) {
        setPickupShiftData(response.data);
      }
    } catch (e) {
      console.error(e);
    }
  }, [selectedDate]);

  useEffect(() => {
    getPickupShiftData();
  }, [getPickupShiftData]);

  const handleChangeDate = (increment) => {
    setSelectedDate((prevDate) => new Date(prevDate.getFullYear(), prevDate.getMonth() + increment, 1));
  };

  const appliedShiftsData = useMemo(() => {
    const appliedShifts = {};

    calendarDaysData.forEach((day) => {
      day.cells?.forEach((cell) => {
        if (cell.assignment_requests?.some((request) => request.user_id === user.id)) {
          const formattedDate = dayjs(getDateFromString(cell.date)).format('MMMM, D');

          if (!appliedShifts[formattedDate]) {
            appliedShifts[formattedDate] = [cell];
          } else {
            appliedShifts[formattedDate].push(cell);
          }
        }
      });
    });

    return appliedShifts;
  }, [calendarDaysData, user.id]);

  return (
    <Layout
      title="Scheduling"
      className={cx('pickup-shift-page-layout', {
        isTablet,
        isMobile,
      })}
      breadcrumbs={[
        {
          name: 'Scheduling',
        },
      ]}
    >
      <div className="page-wrapper scheduling-page">
        <Header />
        <div className="month-select">
          <button className="btn btn-outline pagination-btn" onClick={() => handleChangeDate(-1)}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12.8334 6.9974H1.16675M1.16675 6.9974L7.00008 12.8307M1.16675 6.9974L7.00008 1.16406"
                stroke="#344054"
                strokeWidth="1.67"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Previous
          </button>
          <div className="selected-date">
            {selectedDate.toLocaleDateString('en-US', {
              month: 'long',
              year: 'numeric',
            })}
          </div>
          <button className="btn btn-outline pagination-btn" onClick={() => handleChangeDate(1)}>
            Next
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M1.16675 6.9974H12.8334M12.8334 6.9974L7.00008 1.16406M12.8334 6.9974L7.00008 12.8307"
                stroke="#344054"
                strokeWidth="1.67"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="calendar-container">
          <div className="day-name">S{!isMobile && 'un'}</div>
          <div className="day-name">M{!isMobile && 'on'}</div>
          <div className="day-name">T{!isMobile && 'ue'}</div>
          <div className="day-name">W{!isMobile && 'ed'}</div>
          <div className="day-name">T{!isMobile && 'hu'}</div>
          <div className="day-name">F{!isMobile && 'ri'}</div>
          <div className="day-name">S{!isMobile && 'at'}</div>

          {_.chunk(calendarDaysData, 7).map((row, rowIndex) =>
            row.map((item, colIndex) => (
              <div
                className={cx(`calendar-day scrollbar calendar-day-${item.weekDay} row-${rowIndex}`, {
                  hasShifts: !!item.postedSlots?.some((slot) => !slot.isRequested),
                  isTaken: item.cells?.some((cell) => cell.is_taken && cell.is_period_published),
                })}
                key={`${rowIndex}-${colIndex}`}
                onClick={() => (isMobile ? setSelectedDay(item.date) : undefined)}
              >
                {item.date && (
                  <div
                    className={cx(`day-number ${today.toDateString() === item.date.toDateString() ? 'today' : ''}`, {
                      isSelected: selectedDay?.toDateString() === item.date.toDateString(),
                    })}
                  >
                    <div>{item.date.getDate()}</div>
                  </div>
                )}
                <div className="cells-list">
                  {isMobile ? (
                    <>
                      {hasPickupShiftPermission && !!item.postedSlots?.some((slot) => !slot.isRequested) && (
                        <span className="shifts-dot">.</span>
                      )}
                      {item.cells?.some((cell) => cell.is_taken_by_approve && cell.is_period_published) ? (
                        <span className="approved-shifts-dot">.</span>
                      ) : (
                        !!item.cells?.some((cell) => !!cell.assignment_requests?.some((request) => request.user_id === user.id)) && (
                          <span className="assignment-requests-dot">.</span>
                        )
                      )}
                    </>
                  ) : (
                    <>
                      {hasPickupShiftPermission &&
                        item.postedSlots
                          ?.filter((slot) => isSlotOpenForUser(slot, item.takenShiftsData))
                          .map((slot) => (
                            <div
                              key={slot.key}
                              className="cells-list-item"
                              onClick={() => setConfirmPickupData({ cell: slot.pickupCell, half: slot.half })}
                            >
                              <div className="cells-list-role-name">
                                {slot.role == 'nurse' ? roleLabels?.nurse_role_label : roleLabels?.cna_role_label}
                              </div>
                              <div className="cells-list-shift-info">
                                shift {convertTimeRangeTo12Hour(slot.shiftName)}
                                {slot.label}
                              </div>
                            </div>
                          ))}
                    </>
                  )}
                </div>
              </div>
            )),
          )}
        </div>

        {isMobile && (
          <>
            {selectedDay && selectedDayCellsData.cells.length > 0 ? (
              <ul className="pickup-shifts-list">
                {selectedDayCellsData.cells.map((cell) => (
                  <>
                    {cell.assignment_requests?.some((request) => request.user_id === user.id && request.type === 'first_half') && (
                      <li className="pickup-shifts-list-item is-applied">
                        <div className="pickup-shifts-list-item-name-wrapper">
                          <span className="pickup-shifts-list-item-name">
                            <span className="pickup-shifts-list-item-role-name">
                              {cell.role == 'nurse' ? roleLabels?.nurse_role_label : roleLabels?.cna_role_label}
                            </span>{' '}
                            (applied)
                          </span>
                          <span className="pickup-shifts-list-item-shift">shift {convertTimeRangeTo12Hour(cell.shift_name)} / 1 half</span>
                        </div>
                      </li>
                    )}

                    {cell.assignment_requests?.some((request) => request.user_id === user.id && request.type === 'second half') && (
                      <li className="pickup-shifts-list-item is-applied">
                        <div className="pickup-shifts-list-item-name-wrapper">
                          <span className="pickup-shifts-list-item-name">
                            <span className="pickup-shifts-list-item-role-name">
                              {cell.role == 'nurse' ? roleLabels?.nurse_role_label : roleLabels?.cna_role_label}
                            </span>{' '}
                            (applied)
                          </span>
                          <span className="pickup-shifts-list-item-shift">shift {convertTimeRangeTo12Hour(cell.shift_name)} / 2 half</span>
                        </div>
                      </li>
                    )}

                    {cell.assignment_requests?.some((request) => request.user_id === user.id && request.type === 'both_halves') && (
                      <li className="pickup-shifts-list-item is-applied">
                        <div className="pickup-shifts-list-item-name-wrapper">
                          <span className="pickup-shifts-list-item-name">
                            <span className="pickup-shifts-list-item-role-name">
                              {cell.role == 'nurse' ? roleLabels?.nurse_role_label : roleLabels?.cna_role_label}
                            </span>{' '}
                            (applied)
                          </span>
                          <span className="pickup-shifts-list-item-shift">shift {convertTimeRangeTo12Hour(cell.shift_name)}</span>
                        </div>
                      </li>
                    )}
                  </>
                ))}
                {selectedDayCellsData.cells.map((cell) => (
                  <>
                    {cell.both_user_id === user.id && cell.is_period_published && (
                      <li className="pickup-shifts-list-item is-taken">
                        <div className="pickup-shifts-list-item-name-wrapper">
                          <span className="pickup-shifts-list-item-name">
                            <span className="pickup-shifts-list-item-role-name">
                              {cell.role == 'nurse' ? roleLabels?.nurse_role_label : roleLabels?.cna_role_label}
                            </span>{' '}
                            (my shift)
                          </span>
                          <span className="pickup-shifts-list-item-shift">shift {convertTimeRangeTo12Hour(cell.shift_name)}</span>
                        </div>
                        {cell.both_user_notes && (
                          <div className="pickup-shifts-list-item-note-wrapper">
                            <div>
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                  d="M8 10.4L8 8M8 5.6H7.994M2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8Z"
                                  stroke="#101828"
                                  strokeWidth="1.3"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                            <div>{cell.both_user_notes}</div>
                          </div>
                        )}
                      </li>
                    )}
                    {cell.first_user_id === user.id && cell.is_period_published && (
                      <li className="pickup-shifts-list-item is-taken">
                        <div className="pickup-shifts-list-item-name-wrapper">
                          <span className="pickup-shifts-list-item-name">
                            <span className="pickup-shifts-list-item-role-name">
                              {cell.role == 'nurse' ? roleLabels?.nurse_role_label : roleLabels?.cna_role_label}
                            </span>{' '}
                            (my shift)
                          </span>
                          <span className="pickup-shifts-list-item-shift">shift {convertTimeRangeTo12Hour(cell.shift_name)} / 1 half</span>
                        </div>
                        {cell.first_user_notes && (
                          <div className="pickup-shifts-list-item-note-wrapper">
                            <div>
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                  d="M8 10.4L8 8M8 5.6H7.994M2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8Z"
                                  stroke="#101828"
                                  strokeWidth="1.3"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                            <div>{cell.first_user_notes}</div>
                          </div>
                        )}
                      </li>
                    )}
                    {cell.second_user_id === user.id && cell.is_period_published && (
                      <li className="pickup-shifts-list-item is-taken">
                        <div className="pickup-shifts-list-item-name-wrapper">
                          <span className="pickup-shifts-list-item-name">
                            <span className="pickup-shifts-list-item-role-name">
                              {cell.role == 'nurse' ? roleLabels?.nurse_role_label : roleLabels?.cna_role_label}
                            </span>{' '}
                            (my shift)
                          </span>
                          <span className="pickup-shifts-list-item-shift">shift {convertTimeRangeTo12Hour(cell.shift_name)} / 2 half</span>
                        </div>
                        {cell.second_user_notes && (
                          <div className="pickup-shifts-list-item-note-wrapper">
                            <div>
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                  d="M8 10.4L8 8M8 5.6H7.994M2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8Z"
                                  stroke="#101828"
                                  strokeWidth="1.3"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                            <div>{cell.second_user_notes}</div>
                          </div>
                        )}
                      </li>
                    )}
                  </>
                ))}
                {hasPickupShiftPermission &&
                  selectedDayCellsData.postedSlots
                    .filter((slot) => isSlotOpenForUser(slot, selectedDayCellsData.takenShiftsForDayData))
                    .map((slot) => (
                      <li
                        key={slot.key}
                        className="pickup-shifts-list-item"
                        onClick={() => setConfirmPickupData({ cell: slot.pickupCell, half: slot.half })}
                      >
                        <div className="pickup-shifts-list-item-name-wrapper">
                          <span className="pickup-shifts-list-item-name">
                            <span className="pickup-shifts-list-item-role-name">
                              {slot.role == 'nurse' ? roleLabels?.nurse_role_label : roleLabels?.cna_role_label}
                            </span>{' '}
                            <span style={{ color: '#667085' }}>(open shift)</span>
                          </span>
                          <span className="pickup-shifts-list-item-shift">
                            shift {convertTimeRangeTo12Hour(slot.shiftName)}
                            {slot.label}
                          </span>
                        </div>
                      </li>
                    ))}
              </ul>
            ) : (
              <>
                <ul className="applied-shifts-list">
                  {Object.keys(appliedShiftsData).map((date) => (
                    <li className="applied-shifts-list-item" key={date}>
                      <p className="applied-shifts-list-item-date">{date}</p>
                      {appliedShiftsData[date].map((cell) => {
                        const firstHalfRequest = cell.assignment_requests?.find(
                          (request) => request.type === 'first_half' && request.user_id === user.id,
                        );
                        const secondHalfRequest = cell.assignment_requests?.find(
                          (request) => request.type === 'second_half' && request.user_id === user.id,
                        );
                        const bothHalvesRequest = cell.assignment_requests?.find(
                          (request) => request.type === 'both_halves' && request.user_id === user.id,
                        );

                        return (
                          <>
                            {firstHalfRequest && (
                              <div className="applied-shifts-list-item-cell">
                                <span className="applied-shifts-list-item-name">
                                  <span className="applied-shifts-list-item-name-role">
                                    {cell.role == 'nurse' ? roleLabels?.nurse_role_label : roleLabels?.cna_role_label}
                                  </span>{' '}
                                  (applied)
                                </span>
                                <span className="applied-shifts-list-item-shift">
                                  shift {convertTimeRangeTo12Hour(cell.shift_name)} / 1 half
                                </span>
                              </div>
                            )}

                            {secondHalfRequest && (
                              <div className="applied-shifts-list-item-cell">
                                <span className="applied-shifts-list-item-name">
                                  <span className="applied-shifts-list-item-name-role">
                                    {cell.role == 'nurse' ? roleLabels?.nurse_role_label : roleLabels?.cna_role_label}
                                  </span>{' '}
                                  (applied)
                                </span>
                                <span className="applied-shifts-list-item-shift">
                                  shift {convertTimeRangeTo12Hour(cell.shift_name)} / 2 half
                                </span>
                              </div>
                            )}

                            {bothHalvesRequest && (
                              <div className="applied-shifts-list-item-cell">
                                <span className="applied-shifts-list-item-name">
                                  <span className="applied-shifts-list-item-name-role">
                                    {cell.role == 'nurse' ? roleLabels?.nurse_role_label : roleLabels?.cna_role_label}
                                  </span>{' '}
                                  (applied)
                                </span>
                                <span className="applied-shifts-list-item-shift">shift {convertTimeRangeTo12Hour(cell.shift_name)}</span>
                              </div>
                            )}
                          </>
                        );
                      })}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>
      {!!confirmPickupData && (
        <ConfirmPickupShiftModal
          closeModal={() => setConfirmPickupData(null)}
          data={confirmPickupData}
          setPickupShiftData={setPickupShiftData}
        />
      )}
    </Layout>
  );
};

export default PickupShifts;
